/**
 * Entrar: e-mail e senha.
 *
 * É a primeira coisa que a mãe vê, e por isso tem a cara da Ruth antes de ter
 * cara de formulário: o nome em display e o hebraico ao lado, como no menu.
 *
 * **Por que existe senha.** O PRD dizia "sem senha, só link mágico", e a razão
 * era boa: menos uma coisa para lembrar. Só que a tela de pedir link não é lida
 * como "entrar" — o dono do produto abriu, viu um campo de e-mail com "me manda
 * o link" e leu como um formulário de pedir acesso. Se ele leu assim, uma mãe
 * lê também, e uma mãe que acha que está pedindo acesso fecha o app e espera
 * alguém responder.
 *
 * O link não sumiu: virou as duas portas em que ele é bom, o convite de quem
 * ainda não tem conta e o "esqueci minha senha" de quem tem. Nos dois casos ela
 * escolhe a senha aqui dentro, o que quer dizer que a senha de uma mãe nunca
 * passa pela mão de ninguém para chegar até ela.
 *
 * A recusa é a mesma para senha errada e para e-mail que não existe — de
 * propósito: um "não achei esse e-mail" transformaria este campo na lista de
 * quem usa a Ruth, e saber que uma pessoa usa um app de mãe já é informação
 * sobre a vida dela.
 *
 * Quando a URL traz `?token=`, ela não mostra formulário nenhum: a mãe tocou no
 * link do e-mail e já está entrando.
 *
 * **O código tem porta na primeira tela.** O mesmo e-mail traz um número de
 * seis dígitos, e ele existe por causa do iPhone: o app adicionado à tela de
 * início tem armazenamento próprio, e o link do e-mail sempre abre no Safari —
 * a sessão entra no navegador e o app instalado continua nesta tela. Como no
 * iOS a notificação só existe com o app instalado, sem o código o celular dela
 * não recebe aviso nenhum.
 *
 * Por isso "Já tenho um código" fica logo abaixo de "Me manda o link", e não
 * escondido depois dele: quem abre o app pelo ícone **já tem** o código na
 * caixa de entrada. Obrigá-la a pedir outro para então poder digitar o
 * primeiro é obrigá-la a não conseguir.
 *
 * **Aqui não passa pelo `api.ts`.** O cliente de lá carimba toda chamada com a
 * identificação da usuária, que é justamente o que esta tela ainda não tem —
 * ela é anterior à sessão. Por isso `fetch` direto nas duas rotas.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'

import '../estilos/entrar.css'

type Props = {
  /** A sessão da mãe chegou. Quem monta o app guarda e segue. */
  aoEntrar: (sessao: string) => void
}

type Momento = 'senha' | 'esqueci' | 'mandado' | 'codigo' | 'confirmando' | 'criar-senha'

type Mandei = { mandei: boolean; link: string | null }
type Codigo = { email: string; codigo: string }
type Entrada = { sessao: string; nome_tratamento: string; precisa_criar_senha: boolean }

const ESQUECI = 'Me diz seu e-mail que eu te mando um link pra você criar sua senha.'
const MANDADO = 'Se esse e-mail estiver cadastrado, o link já saiu. Ele vale por uma hora.'
const CRIAR_SENHA = 'Agora escolhe uma senha pra entrar direto das próximas vezes.'
const MINIMO_DA_SENHA = 8
const ENTRANDO = 'Só um instante. Estou te colocando pra dentro.'
const SEM_RESPOSTA = 'Não consegui falar com a Ruth agora. Tenta de novo daqui a pouco.'
const CODIGO_CONVITE =
  'Abriu pelo ícone na tela do celular? O link do e-mail abre o navegador, não este app. Digita aqui o código de 6 números que veio no mesmo e-mail.'
const CODIGO_ROTULO = 'Código de 6 números'
const DIGITOS = 6

/** A recusa do servidor com o número junto: a tela decide pelo número. */
class Recusa extends Error {
  constructor(
    mensagem: string,
    readonly status: number,
  ) {
    super(mensagem)
  }
}

async function pedirA<T>(caminho: string, corpo: unknown): Promise<T> {
  const resposta = await fetch(caminho, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  })
  if (!resposta.ok) {
    const erro = await resposta.json().catch(() => ({}))
    throw new Recusa(erro.detail ?? SEM_RESPOSTA, resposta.status)
  }
  return resposta.json() as Promise<T>
}

function tokenDaUrl(): string | null {
  return new URLSearchParams(window.location.search).get('token')
}

/** O token não fica na barra de endereço: recarregar a página gastaria o link. */
function limparAUrl(): void {
  window.history.replaceState(null, '', window.location.pathname)
}

export function Entrar({ aoEntrar }: Props) {
  const [momento, setMomento] = useState<Momento>(() => (tokenDaUrl() ? 'confirmando' : 'senha'))
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  /** A sessão que o e-mail abriu, guardada até ela escolher a senha. */
  const [sessaoAberta, setSessaoAberta] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [atalho, setAtalho] = useState<string | null>(null)
  const jaConfirmou = useRef(false)

  useEffect(() => {
    const token = tokenDaUrl()
    if (!token || jaConfirmou.current) return
    // O React monta duas vezes em desenvolvimento (StrictMode). Sem esta trava,
    // a segunda montagem gasta o link e a mãe lê "esse link já foi usado" sobre
    // o link que acabou de chegar.
    jaConfirmou.current = true

    void (async () => {
      try {
        const entrada = await pedirA<Entrada>('/api/entrar/confirmar', { token })
        limparAUrl()
        // Quem entrou pelo link e ainda não tem senha escolhe agora. É o
        // convite e o "esqueci a senha" caindo no mesmo lugar.
        if (entrada.precisa_criar_senha) {
          setSessaoAberta(entrada.sessao)
          setMomento('criar-senha')
          return
        }
        aoEntrar(entrada.sessao)
      } catch (e) {
        limparAUrl()
        setErro(e instanceof Error ? e.message : SEM_RESPOSTA)
        setMomento('senha')
      }
    })()
  }, [aoEntrar])

  const pedirOLink = useCallback(
    async (evento: FormEvent) => {
      evento.preventDefault()
      const endereco = email.trim()
      if (!endereco || enviando) return

      setEnviando(true)
      setErro(null)
      try {
        const resposta = await pedirA<Mandei>('/api/entrar', { email: endereco })
        // `link` só vem fora de produção, para dar para entrar sem servidor de
        // e-mail montado. Em produção a API devolve nulo e nada aparece aqui.
        setAtalho(resposta.link)
        setMomento('mandado')
      } catch (e) {
        setErro(e instanceof Error ? e.message : SEM_RESPOSTA)
      } finally {
        setEnviando(false)
      }
    },
    [email, enviando],
  )

  const recomecar = useCallback(() => {
    setAtalho(null)
    setErro(null)
    setMomento('senha')
  }, [])

  const entrarComSenha = useCallback(
    async (evento: FormEvent) => {
      evento.preventDefault()
      if (!email.trim() || !senha || enviando) return

      setEnviando(true)
      setErro(null)
      try {
        const entrada = await pedirA<Entrada>('/api/entrar/senha', {
          email: email.trim(),
          senha,
        })
        setSenha('')
        aoEntrar(entrada.sessao)
      } catch (e) {
        // Conta que existe e ainda não tem senha: é a mãe recém-convidada, e
        // mandá-la procurar "esqueci minha senha" seria mandá-la procurar uma
        // senha que ela nunca teve. A tela leva ela direto.
        if (e instanceof Recusa && e.status === 409) {
          setSenha('')
          setErro(null)
          setMomento('esqueci')
          return
        }
        setErro(e instanceof Error ? e.message : SEM_RESPOSTA)
      } finally {
        setEnviando(false)
      }
    },
    [email, senha, enviando, aoEntrar],
  )

  /** A senha escolhida por ela, dentro da sessão que o e-mail já abriu. */
  const guardarASenha = useCallback(
    async (nova: string) => {
      if (!sessaoAberta) return
      await fetch('/api/entrar/senha-nova', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessaoAberta}`,
        },
        body: JSON.stringify({ senha: nova }),
      }).then(async (r) => {
        if (!r.ok) {
          const erro = await r.json().catch(() => ({}))
          throw new Error(erro.detail ?? SEM_RESPOSTA)
        }
      })
      aoEntrar(sessaoAberta)
    },
    [sessaoAberta, aoEntrar],
  )

  const entrarComOCodigo = useCallback(
    async (codigo: string) => {
      setErro(null)
      const entrada = await pedirA<Entrada>('/api/entrar/codigo', {
        email: email.trim(),
        codigo,
      } satisfies Codigo)
      aoEntrar(entrada.sessao)
    },
    [email, aoEntrar],
  )

  return (
    <div className="entrar">
      <div className="entrar__caixa">
        <div className="entrar__marca">
          <span className="entrar__nome">Ruth</span>
          <span className="entrar__hebraico" lang="he">
            רות
          </span>
        </div>

        {momento === 'confirmando' && (
          <p className="entrar__frase" role="status">
            {ENTRANDO}
          </p>
        )}

        {momento === 'mandado' && (
          <Espera atalho={atalho} aoRecomecar={recomecar} aoUsarCodigo={entrarComOCodigo} />
        )}

        {momento === 'senha' && (
          <Formulario
            email={email}
            senha={senha}
            enviando={enviando}
            aoDigitarEmail={setEmail}
            aoDigitarSenha={setSenha}
            aoEntrar={entrarComSenha}
            aoEsquecer={() => {
              setErro(null)
              setMomento('esqueci')
            }}
            aoUsarCodigo={() => setMomento('codigo')}
          />
        )}

        {momento === 'esqueci' && (
          <Esqueci
            email={email}
            enviando={enviando}
            aoDigitar={setEmail}
            aoMandar={pedirOLink}
            aoVoltar={recomecar}
          />
        )}

        {momento === 'criar-senha' && <CriarSenha aoGuardar={guardarASenha} />}

        {momento === 'codigo' && (
          <PorCodigo
            email={email}
            aoDigitarEmail={setEmail}
            aoUsar={entrarComOCodigo}
            aoVoltar={recomecar}
          />
        )}

        {erro && (
          <p className="entrar__erro" role="alert">
            {erro}
          </p>
        )}
      </div>
    </div>
  )
}

function Formulario({
  email,
  senha,
  enviando,
  aoDigitarEmail,
  aoDigitarSenha,
  aoEntrar,
  aoEsquecer,
  aoUsarCodigo,
}: {
  email: string
  senha: string
  enviando: boolean
  aoDigitarEmail: (valor: string) => void
  aoDigitarSenha: (valor: string) => void
  aoEntrar: (evento: FormEvent) => void
  aoEsquecer: () => void
  aoUsarCodigo: () => void
}) {
  return (
    <form className="entrar__forma" onSubmit={aoEntrar}>
      <label className="entrar__rotulo" htmlFor="entrar-email">
        Seu e-mail
      </label>
      <input
        id="entrar-email"
        className="entrar__campo"
        type="email"
        name="email"
        value={email}
        onChange={(evento) => aoDigitarEmail(evento.target.value)}
        placeholder="voce@exemplo.com"
        autoComplete="email"
        inputMode="email"
        autoFocus
        required
      />

      <label className="entrar__rotulo" htmlFor="entrar-senha">
        Sua senha
      </label>
      <input
        id="entrar-senha"
        className="entrar__campo"
        type="password"
        name="senha"
        value={senha}
        onChange={(evento) => aoDigitarSenha(evento.target.value)}
        autoComplete="current-password"
        enterKeyHint="go"
        required
      />

      <button
        type="submit"
        className="entrar__botao"
        disabled={enviando || !email.trim() || !senha}
      >
        {enviando ? 'Entrando…' : 'Entrar'}
      </button>

      <button type="button" className="entrar__voltar" onClick={aoEsquecer}>
        Esqueci minha senha
      </button>

      <button type="button" className="entrar__voltar" onClick={aoUsarCodigo}>
        Já tenho um código
      </button>
    </form>
  )
}

/**
 * "Esqueci minha senha" — que é o mesmo link mágico de sempre, com outro nome.
 *
 * A frase de resposta é igual para e-mail que existe e para e-mail que não
 * existe, pela mesma razão de antes: esta caixa não pode virar a lista de quem
 * usa a Ruth.
 */
function Esqueci({
  email,
  enviando,
  aoDigitar,
  aoMandar,
  aoVoltar,
}: {
  email: string
  enviando: boolean
  aoDigitar: (valor: string) => void
  aoMandar: (evento: FormEvent) => void
  aoVoltar: () => void
}) {
  return (
    <form className="entrar__forma" onSubmit={aoMandar}>
      <p className="entrar__frase">{ESQUECI}</p>

      <label className="entrar__rotulo" htmlFor="entrar-email-esqueci">
        Seu e-mail
      </label>
      <input
        id="entrar-email-esqueci"
        className="entrar__campo"
        type="email"
        name="email"
        value={email}
        onChange={(evento) => aoDigitar(evento.target.value)}
        placeholder="voce@exemplo.com"
        autoComplete="email"
        inputMode="email"
        enterKeyHint="send"
        autoFocus
        required
      />

      <button type="submit" className="entrar__botao" disabled={enviando || !email.trim()}>
        Me manda o link
      </button>

      <button type="button" className="entrar__voltar" onClick={aoVoltar}>
        Voltar
      </button>
    </form>
  )
}

/**
 * A senha que ela escolhe, já do lado de dentro.
 *
 * Pede duas vezes porque uma senha digitada errada e guardada é a mãe trancada
 * fora da própria casa até pedir outro link — e ela não teria como saber que
 * errou.
 */
function CriarSenha({ aoGuardar }: { aoGuardar: (senha: string) => Promise<void> }) {
  const [senha, setSenha] = useState('')
  const [repetida, setRepetida] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)

  const guardar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando) return

    if (senha.length < MINIMO_DA_SENHA) {
      setAviso(`A senha precisa de pelo menos ${MINIMO_DA_SENHA} letras ou números.`)
      return
    }
    if (senha !== repetida) {
      setAviso('As duas não são iguais. Confere pra mim?')
      return
    }

    setSalvando(true)
    setAviso(null)
    try {
      await aoGuardar(senha)
    } catch (e) {
      setAviso(e instanceof Error ? e.message : SEM_RESPOSTA)
      setSalvando(false)
    }
  }

  return (
    <form className="entrar__forma" onSubmit={guardar}>
      <p className="entrar__frase">{CRIAR_SENHA}</p>

      <label className="entrar__rotulo" htmlFor="entrar-senha-nova">
        Sua senha
      </label>
      <input
        id="entrar-senha-nova"
        className="entrar__campo"
        type="password"
        value={senha}
        onChange={(evento) => setSenha(evento.target.value)}
        autoComplete="new-password"
        autoFocus
        required
      />

      <label className="entrar__rotulo" htmlFor="entrar-senha-repetida">
        De novo, pra conferir
      </label>
      <input
        id="entrar-senha-repetida"
        className="entrar__campo"
        type="password"
        value={repetida}
        onChange={(evento) => setRepetida(evento.target.value)}
        autoComplete="new-password"
        enterKeyHint="go"
        required
      />

      {aviso && (
        <p className="entrar__erro" role="alert">
          {aviso}
        </p>
      )}

      <button type="submit" className="entrar__botao" disabled={salvando}>
        {salvando ? 'Guardando…' : 'Guardar e entrar'}
      </button>
    </form>
  )
}

/**
 * O caminho de quem abriu a Ruth pelo ícone e já tem o código no e-mail.
 *
 * Pede o e-mail junto porque o código sozinho não diz de quem é — é assim que
 * o de uma mãe não abre a casa da outra.
 */
function PorCodigo({
  email,
  aoDigitarEmail,
  aoUsar,
  aoVoltar,
}: {
  email: string
  aoDigitarEmail: (valor: string) => void
  aoUsar: (codigo: string) => Promise<void>
  aoVoltar: () => void
}) {
  return (
    <>
      <p className="entrar__frase">{CODIGO_CONVITE}</p>

      <label className="entrar__rotulo" htmlFor="entrar-email-codigo">
        Seu e-mail
      </label>
      <input
        id="entrar-email-codigo"
        className="entrar__campo"
        type="email"
        name="email"
        value={email}
        onChange={(evento) => aoDigitarEmail(evento.target.value)}
        placeholder="voce@exemplo.com"
        autoComplete="email"
        inputMode="email"
        autoFocus={!email.trim()}
        required
      />

      <CampoDoCodigo aoUsar={aoUsar} semRotulo />

      <button type="button" className="entrar__voltar" onClick={aoVoltar}>
        Me manda um código novo
      </button>
    </>
  )
}

function Espera({
  atalho,
  aoRecomecar,
  aoUsarCodigo,
}: {
  atalho: string | null
  aoRecomecar: () => void
  aoUsarCodigo: (codigo: string) => Promise<void>
}) {
  return (
    <>
      <p className="entrar__frase" role="status">
        {MANDADO}
      </p>

      {atalho && (
        <a className="entrar__atalho" href={atalho}>
          Entrar agora (atalho de desenvolvimento)
        </a>
      )}

      <CampoDoCodigo aoUsar={aoUsarCodigo} />

      <button type="button" className="entrar__voltar" onClick={aoRecomecar}>
        Usar outro e-mail
      </button>
    </>
  )
}

/**
 * Os seis dígitos, para quem abriu a Ruth pelo ícone.
 *
 * `one-time-code` faz o iPhone oferecer o número que acabou de chegar, em vez
 * de ela ir buscar no e-mail e voltar. `inputMode` numérico abre o teclado de
 * números — com uma criança no colo, cada toque a menos conta.
 */
function CampoDoCodigo({
  aoUsar,
  semRotulo,
}: {
  aoUsar: (codigo: string) => Promise<void>
  /** Em `PorCodigo` a explicação já está em cima; aqui basta nomear o campo. */
  semRotulo?: boolean
}) {
  const [codigo, setCodigo] = useState('')
  const [entrando, setEntrando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const digitos = codigo.replace(/\D/g, '')

  const mandar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (digitos.length !== DIGITOS || entrando) return

    setEntrando(true)
    setErro(null)
    try {
      await aoUsar(digitos)
    } catch (e) {
      setErro(e instanceof Error ? e.message : SEM_RESPOSTA)
    } finally {
      setEntrando(false)
    }
  }

  return (
    <form className="entrar__codigo" onSubmit={mandar}>
      <label className="entrar__rotulo" htmlFor="entrar-codigo">
        {semRotulo ? CODIGO_ROTULO : CODIGO_CONVITE}
      </label>
      <input
        id="entrar-codigo"
        className="entrar__campo entrar__campo--codigo"
        type="text"
        name="codigo"
        value={codigo}
        onChange={(evento) => setCodigo(evento.target.value)}
        placeholder="000000"
        autoComplete="one-time-code"
        inputMode="numeric"
        enterKeyHint="go"
        maxLength={DIGITOS + 2}
      />

      <button
        type="submit"
        className="entrar__botao"
        disabled={entrando || digitos.length !== DIGITOS}
      >
        {entrando ? 'Entrando…' : 'Entrar com o código'}
      </button>

      {erro && (
        <p className="entrar__erro" role="alert">
          {erro}
        </p>
      )}
    </form>
  )
}
